import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import React, { useEffect, useMemo, useRef } from "react";
import Chart from "react-google-charts";
import { GoogleChartWrapper, GoogleViz } from "react-google-charts/dist/types";
import { getVAxisWidth, INSIDE_V_AXIS_CHART_AREA } from "utils/helpers";
import theme from "../../../Theme/AppTheme";
import { defaultColorScheme } from "./GraphColorSchemes";
import { XValueType } from "utils/ReportDataToGoogleFormat";

const chartName = "AcxVerticalBar";

type GoogleDataFormat = any[][];

type AxesScaleTypes = "log" | "mirrorLog" | null;

// see: https://developers.google.com/chart/interactive/docs/customizing_axes#number-formats
type NumberFormatPreset =
    | "none"
    | "decimal"
    | "scientific"
    | "currency"
    | "percent"
    | "short"
    | "long";

interface Props {
    data?: GoogleDataFormat;
    previousData?: GoogleDataFormat;

    colorScheme?: string[]; // not actually used since the utility reportDataToGoogleFormat styles each bar based on
    // theme colors

    legendPosition: "none" | "bottom" | "in" | "right" | "top";
    legendAlignment?: "start" | "center" | "end";
    showGrid?: boolean;
    forceLegend?: boolean;
    yTitle?: string;
    xTitle?: string;
    fontSize?: string;
    fontBold?: boolean;
    fontItalic?: boolean;
    fontColor?: string;
    dataOpacity?: number;
    hAxisFontSize?: string;
    vAxisFontSize?: string;
    vAxisFormat?: NumberFormatPreset;
    vAxisTitleStyle?: Record<string, unknown>;
    vAxisTextStyle?: Record<string, unknown>;
    vAxisMaxValue?: number;
    /**
     * Draws the value axis's numbers inside the plot, so a narrow chart keeps
     * them without giving up plot width.
     */
    vAxisTextInside?: boolean;

    onSelect?: (
        series: string | null,
        x: XValueType | null,
        y: string | number | null | any,
        chartId?: string,
        seriesIdentifier?: string | null,
    ) => void;

    chartAreaLeftOffset?: number | string;
    chartAreaTopOffset?: number | string;
    chartAreaHeight?: number | string;
    chartAreaWidth?: number | string;

    showDemoData?: boolean;
    asWidget?: boolean;

    slantedText?: boolean;
    slantedTextAngle?: number;

    enableZoom?: boolean;
    stacked?: boolean;
    verticalScaleType?: AxesScaleTypes;
    horizontalScaleType?: AxesScaleTypes;

    onChartReady?: (
        chartWrapper: GoogleChartWrapper,
        google: GoogleViz,
    ) => void;

    chartId?: string;
    chartHeight?: string;

    usesHtmlTooltips?: boolean;
    disableAnimation?: boolean;
}

const AcxVerticalBarGraph: React.FC<Props> = (props) => {
    const wrapper = useRef<null | any>();
    useEffect(() => {
        return () => {
            wrapper.current?.getChart()?.clearChart();
        };
    }, []);

    const data = Boolean(props.showDemoData) ? fake_data : props.data;

    const showLegendFromData =
        (data?.[0].filter((d) => !Boolean(d.role)).length ?? 0) > 2;
    const showLegend =
        props.forceLegend !== undefined
            ? props.forceLegend
            : showLegendFromData;

    const options = useMemo(() => {
        const shouldAnimate = !props.disableAnimation;
        const res = {
            fontName: "Inter",
            annotations: {
                alwaysOutside: true,
                textStyle: {
                    fontName: "Inter",
                    fontSize: props.fontSize ?? 18,
                    bold: props.fontBold ?? true,
                    italic: props.fontItalic ?? true,
                    // The color of the text.
                    color: props.fontColor ?? "#000",
                    // The color of the text outline.
                    auraColor: "transparent",
                    // The transparency of the text.
                    // opacity: 0.6
                },
            },
            dataOpacity: props.dataOpacity ?? 0.65,
            isStacked: Boolean(props.stacked),
            animation: shouldAnimate
                ? {
                      startup: true,
                      duration: 500,
                      easing: "out",
                  }
                : {
                      startup: false,
                      duration: 0,
                  },
            interpolateNulls: true,
            colors:
                props.colorScheme && props.colorScheme.length
                    ? props.colorScheme
                    : defaultColorScheme,
            tooltip: {
                textStyle: { fontName: "Inter" },
                isHtml: !!props.usesHtmlTooltips,
            },
            legend: {
                position: showLegend ? props.legendPosition : "none",
                alignment: props.legendAlignment
                    ? props.legendAlignment
                    : "start",
                textStyle: {
                    fontName: "Inter",
                },
                maxLines: 5,
            },
            chart: {},
            chartArea: {
                ...(props.vAxisTextInside
                    ? INSIDE_V_AXIS_CHART_AREA
                    : {
                          left: props.vAxisFontSize
                              ? getVAxisWidth(props.vAxisFontSize)
                              : props.chartAreaLeftOffset ??
                                (props.asWidget ? "8%" : "4%"),
                          width:
                              props.chartAreaWidth ??
                              (props.asWidget ? "88%" : "90%"),
                      }),
                top: props.chartAreaTopOffset ?? (props.asWidget ? "7%" : "5%"),
                height: props.chartAreaHeight
                    ? `${props.chartAreaHeight.toString().replace("%", "")}%`
                    : props.asWidget
                    ? "77%"
                    : "90%",
            },
            hAxis: {
                // textPosition: props.asWidget ? undefined : "in",
                scaleType: null as AxesScaleTypes,
                // title: props.xTitle,
                slantedText:
                    props.slantedText !== undefined
                        ? props.slantedText
                        : props.asWidget
                        ? undefined
                        : true,
                slantedTextAngle:
                    props.slantedTextAngle ?? (props.asWidget ? undefined : 15),
                maxAlternation: 3,
                showTextEvery: 1,
                titleTextStyle: {
                    color: theme.palette.grey[300],
                    auraColor: "none",
                    fontSize: props.asWidget ? 13 : 16,
                    fontName: "Inter Bold",
                },
                textStyle: {
                    color: theme.palette.gray[400],
                    auraColor: "none",
                    fontSize: props.asWidget ? 8 : props.hAxisFontSize ?? 11,
                    fontName: "Inter Bold",
                },
                minorGridLines: {
                    count: 0,
                },
                gridLines: {},
                // viewWindowMode:"pretty",
            },
            vAxis: {
                scaleType: null as AxesScaleTypes,
                title: props.yTitle,
                textPosition: props.vAxisTextInside ? "in" : "out",
                titleTextStyle: {
                    color: theme.palette.gray[400],
                    auraColor: "none",
                    italic: false,
                    fontSize: props.asWidget ? 13 : 16,
                    fontName: "Inter Bold",
                    ...props.vAxisTitleStyle,
                },
                textStyle: {
                    color: theme.palette.gray[400],
                    auraColor: "none",
                    fontSize: props.asWidget ? 10 : props.vAxisFontSize ?? 13,
                    fontName: "Inter Bold",
                    ...props.vAxisTextStyle,
                },
                minorGridLines: {
                    count: 0,
                },
                gridlines: {
                    color: props.showGrid ? "E7E7E7" : "none",
                },
                minValue: 0,
                maxValue: props.vAxisMaxValue,
                format: props.vAxisFormat,
            },

            explorer: undefined as any | undefined,

            enableInteractivity: true,
        };

        res.vAxis.scaleType = props.verticalScaleType ?? null;
        res.hAxis.scaleType = props.horizontalScaleType ?? null;

        return res;
    }, [
        props.asWidget,
        props.chartAreaHeight,
        props.chartAreaLeftOffset,
        props.chartAreaTopOffset,
        props.chartAreaWidth,
        props.colorScheme,
        props.dataOpacity,
        props.fontBold,
        props.fontColor,
        props.fontItalic,
        props.fontSize,
        props.hAxisFontSize,
        props.horizontalScaleType,
        props.legendAlignment,
        props.legendPosition,
        props.showGrid,
        props.slantedText,
        props.slantedTextAngle,
        props.stacked,
        props.usesHtmlTooltips,
        props.vAxisFontSize,
        props.verticalScaleType,
        props.yTitle,
        props.vAxisFormat,
        props.vAxisTitleStyle,
        props.vAxisTextStyle,
        props.vAxisMaxValue,
        props.vAxisTextInside,
        showLegend,
        props.disableAnimation,
    ]);

    // chartEvents callbacks are memoized and can only access props from initial render
    // this ref stores an updated data prop for use in the chartEvent select callback
    const dataRef = useRef({ data: props.data });

    useEffect(() => {
        dataRef.current = { data: props.data };
    }, [props.data]);

    return (
        <>
            {Boolean(data) && (
                <Chart
                    width={`100%`}
                    height={
                        props.chartHeight
                            ? props.chartHeight
                            : props.asWidget
                            ? `97%`
                            : "100%"
                    }
                    chartType="ColumnChart"
                    loader={
                        <AcxLoadingIndicator
                            color="secondary"
                            alternate="PuffLoader"
                            size={96}
                            style={
                                props.chartHeight
                                    ? { height: props.chartHeight }
                                    : {}
                            }
                        />
                    }
                    data={Boolean(props.previousData) ? undefined : data}
                    diffdata={
                        Boolean(props.previousData)
                            ? {
                                  old: props.previousData,
                                  new: props.data,
                              }
                            : undefined
                    }
                    options={options}
                    chartEvents={[
                        {
                            eventName: "select",
                            callback: ({ chartWrapper }) => {
                                const chart = chartWrapper.getChart();
                                const selection = chart.getSelection();

                                if (selection.length === 1) {
                                    const [selectedItem] = selection;
                                    const dataTable =
                                        chartWrapper.getDataTable();
                                    const { row, column } = selectedItem;

                                    if (row !== null && column !== null) {
                                        const rowLabel =
                                            dataRef.current.data?.[
                                                row + 1
                                            ]?.[0];

                                        props.onSelect?.(
                                            dataTable?.getColumnLabel(column) ??
                                                null,
                                            rowLabel,
                                            dataTable?.getValue(row, column) ??
                                                null,
                                            // row,
                                            props.chartId,
                                            dataTable?.getColumnId(column) ??
                                                null,
                                        );
                                    } else if (row !== null) {
                                        const rowLabel =
                                            dataRef.current.data?.[
                                                row + 1
                                            ]?.[0];
                                        console.log(
                                            `${chartName} selection : ${JSON.stringify(
                                                {
                                                    row,
                                                    column,
                                                    X: rowLabel,
                                                    rowProps:
                                                        dataTable?.getRowProperties(
                                                            row,
                                                        ),
                                                },
                                            )}`,
                                        );
                                        props.onSelect?.(
                                            null,
                                            rowLabel,
                                            null,
                                            // row,
                                            props.chartId,
                                            null,
                                        );
                                    } else if (column !== null) {
                                        console.log(
                                            `${chartName} selection : ${JSON.stringify(
                                                {
                                                    row,
                                                    column,
                                                    series: dataTable?.getColumnLabel(
                                                        column,
                                                    ),
                                                    columnProps:
                                                        dataTable?.getColumnProperties(
                                                            column,
                                                        ),
                                                },
                                            )}`,
                                        );
                                        props.onSelect?.(
                                            dataTable?.getColumnLabel(column) ??
                                                null,
                                            null,
                                            // null,
                                            null,
                                        );
                                    }
                                }
                            },
                        },
                    ]}
                    getChartWrapper={(w, g) => {
                        wrapper.current = w;
                        props.onChartReady?.(w, g);
                    }}
                />
            )}
        </>
    );
};

const fake_data = [
    ["Tags", "Count"],
    ["Help With Bill", 8.94],
    ["Brand Detractor", 10.49],
    ["Program Value", 19.3],
    ["Brand Value", 21.45],
];

export default AcxVerticalBarGraph;
